/**
 * @license
 * Copyright 2026 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 *
 * g3-prettier-ignore-file
 */

/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { SDKHooks } from "../hooks/hooks.js";
import { HookContext } from "../hooks/types.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/http-client-errors.js";
import { ERR, OK, Result } from "../types/fp.js";
import { stringToBase64 } from "./base64.js";
import { SDK_METADATA, SDKOptions, serverURLFromOptions } from "./config.js";
import { encodeForm } from "./encodings.js";
import { env, fillGlobals } from "./env.js";
import {
  HTTPClient,
  isAbortError,
  isConnectionError,
  isTimeoutError,
  matchContentType,
} from "./http.js";
import { Logger } from "./logger.js";
import { combineSignals, isPlainObject } from "./primitives.js";
import { retry, RetryConfig } from "./retries.js";
import { SecurityState } from "./security.js";

export type RequestOptions = {
  /**
   * Sets a timeout, in milliseconds, on HTTP requests made by an SDK method. If
   * `fetchOptions.signal` is set then it will take precedence over this option.
   */
  timeout_ms?: number;
  /**
   * Set or override a retry policy on HTTP calls.
   */
  retries?: RetryConfig;
  /**
   * Specifies the status codes which should be retried using the given retry policy.
   */
  retry_codes?: string[];
  /**
   * Overrides the base server URL that will be used by an operation.
   */
  server_url?: string | URL;
  /**
   * @deprecated `fetchOptions` has been flattened into `RequestOptions`.
   *
   * Sets various request options on the `fetch` call made by an SDK method.
   *
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Request/Request#options|Request}
   */
  fetch_options?: Omit<RequestInit, "method" | "body">;
  /**
   * Additional query parameters to set on the request URL. Entries replace
   * any query parameters of the same name produced by the operation. Entries
   * with null or undefined values are skipped, array values are encoded as
   * repeated keys and plain object values are JSON-encoded.
   */
  extra_query?: Record<string, unknown>;
  /**
   * Additional fields to merge into the JSON object request body. Entries
   * replace any fields of the same name produced by the operation. Entries
   * with undefined values are skipped. Setting this option on an operation
   * whose request body is not a JSON object results in an error.
   */
  extra_body?: Record<string, unknown>;
} & Omit<RequestInit, "method" | "body">;

type RequestConfig = {
  method: string;
  path: string;
  baseURL?: string | URL | undefined;
  query?: string;
  body?: RequestInit["body"];
  headers?: HeadersInit;
  security?: SecurityState | null;
  uaHeader?: string;
  userAgent?: string | undefined;
  timeout_ms?: number;
};

const gt: unknown = typeof globalThis === "undefined" ? null : globalThis;
const webWorkerLike = typeof gt === "object"
  && gt != null
  && "importScripts" in gt
  && typeof gt["importScripts"] === "function";
const isBrowserLike = webWorkerLike
  || (typeof navigator !== "undefined" && "serviceWorker" in navigator)
  || (typeof window === "object" && typeof window.document !== "undefined");

export class ClientSDK {
  readonly _httpClient: HTTPClient;
  readonly _hooks: SDKHooks;
  readonly _logger?: Logger | undefined;
  public readonly _baseURL: URL | null;
  public readonly _options: SDKOptions & { hooks?: SDKHooks };

  constructor(options: SDKOptions = {}) {
    const opt = options as unknown;
    if (
      typeof opt === "object"
      && opt != null
      && "hooks" in opt
      && opt.hooks instanceof SDKHooks
    ) {
      this._hooks = opt.hooks;
    } else {
      this._hooks = new SDKHooks();
    }
    const defaultHttpClient = new HTTPClient();
    options.http_client = options.http_client || defaultHttpClient;
    options = this._hooks.sdkInit(options);

    const url = serverURLFromOptions(options);
    if (url) {
      url.pathname = url.pathname.replace(/\/+$/, "") + "/";
    }
    this._baseURL = url;
    this._httpClient = options.http_client || defaultHttpClient;

    this._options = { ...fillGlobals(options), hooks: this._hooks };

    this._logger = this._options.debug_logger;
    if (!this._logger && env().GOOGLE_GENAI_DEBUG) {
      this._logger = console;
    }
  }

  public _createRequest(
    context: HookContext,
    conf: RequestConfig,
    options?: RequestOptions,
  ): Result<Request, InvalidRequestError | UnexpectedClientError> {
    const { method, path, query, headers: opHeaders, security } = conf;

    const base = conf.baseURL ?? this._baseURL;
    if (!base) {
      return ERR(new InvalidRequestError("No base URL provided for operation"));
    }
    const baseURL = new URL(base);
    let reqURL: URL;
    if (path) {
      baseURL.pathname = baseURL.pathname.replace(/\/+$/, "") + "/";
      reqURL = new URL(path, baseURL);
      if (!reqURL.search && baseURL.search) {
        reqURL.search = baseURL.search;
      }
    } else {
      reqURL = baseURL;
    }
    reqURL.hash = "";

    // Appends already-encoded query pairs to a query string, replacing any
    // existing pairs with the same key so later sources take precedence.
    const mergeQuery = (current: string, additions: string): string => {
      if (!additions) {
        return current;
      }
      const additionKeys = new Set(
        additions
          .split("&")
          .filter((pair) => pair !== "")
          .map((pair) => pair.split("=")[0] ?? ""),
      );
      const kept = current.split("&").filter((pair) => {
        return pair !== "" && !additionKeys.has(pair.split("=")[0] ?? "");
      });
      return [...kept, additions].join("&");
    };

    const encodeQueryRecord = (record: Record<string, unknown>): string => {
      return Object.entries(record)
        .map(([k, v]) => {
          if (v == null) {
            return undefined;
          }
          const value = isPlainObject(v) ? JSON.stringify(v) : v;
          return encodeForm(k, value, {
            explode: Array.isArray(value),
            charEncoding: "percent",
          });
        })
        .filter((pair): pair is string => typeof pair !== "undefined")
        .join("&");
    };

    const finalQuery = [
      query || "",
      encodeQueryRecord(options?.extra_query || {}),
      encodeQueryRecord(security?.queryParams || {}),
    ].reduce(mergeQuery, reqURL.search.slice(1));

    if (finalQuery) {
      reqURL.search = `?${finalQuery}`;
    }

    const headers = new Headers(opHeaders);

    const username = security?.basic.username;
    const password = security?.basic.password;
    if (username != null || password != null) {
      const encoded = stringToBase64(
        [username || "", password || ""].join(":"),
      );
      headers.set("Authorization", `Basic ${encoded}`);
    }

    const securityHeaders = new Headers(security?.headers || {});
    for (const [k, v] of securityHeaders) {
      headers.set(k, v);
    }

    let cookie = headers.get("cookie") || "";
    for (const [k, v] of Object.entries(security?.cookies || {})) {
      cookie += `; ${k}=${v}`;
    }
    cookie = cookie.startsWith("; ") ? cookie.slice(2) : cookie;
    headers.set("cookie", cookie);

    const userHeaders = new Headers(
      options?.headers ?? options?.fetch_options?.headers,
    );
    for (const [k, v] of userHeaders) {
      headers.set(k, v);
    }

    // Only set user agent header in non-browser-like environments since CORS
    // policy disallows setting it in browsers e.g. Chrome throws an error.
    if (!isBrowserLike) {
      headers.set(
        conf.uaHeader ?? "user-agent",
        conf.userAgent ?? SDK_METADATA.userAgent,
      );
    }

    let reqBody = conf.body;
    const extraBody = Object.fromEntries(
      Object.entries(options?.extra_body || {}).filter(
        ([, v]) => typeof v !== "undefined",
      ),
    );
    if (Object.keys(extraBody).length > 0) {
      const contentType = new Headers(opHeaders).get("content-type") || "";
      const isJSON = /^(application|text)\/([^+]+\+)*json/.test(contentType);
      if (!isJSON || (typeof reqBody !== "string" && reqBody != null)) {
        return ERR(
          new InvalidRequestError(
            "extra_body can only be merged into JSON object request bodies",
          ),
        );
      }
      let parsedBody: unknown;
      try {
        parsedBody = reqBody ? JSON.parse(reqBody) : {};
      } catch (err: unknown) {
        return ERR(
          new InvalidRequestError(
            "extra_body can only be merged into JSON object request bodies",
            { cause: err },
          ),
        );
      }
      if (!isPlainObject(parsedBody)) {
        return ERR(
          new InvalidRequestError(
            "extra_body can only be merged into JSON object request bodies",
          ),
        );
      }
      reqBody = JSON.stringify({ ...parsedBody, ...extraBody });
      headers.delete("content-length");
    }

    const fetchOptions: Omit<RequestInit, "method" | "body"> = {
      ...options?.fetch_options,
      ...options,
    };
    if (
      !fetchOptions?.signal && conf.timeout_ms != null && conf.timeout_ms > 0
    ) {
      context.timeout_ms = conf.timeout_ms;
    }

    if (conf.body instanceof ReadableStream) {
      Object.assign(fetchOptions, { duplex: "half" });
    }

    let input;
    try {
      input = this._hooks.beforeCreateRequest(context, {
        url: reqURL,
        options: {
          ...fetchOptions,
          body: reqBody ?? null,
          headers,
          method,
        },
      });
    } catch (err: unknown) {
      return ERR(
        new UnexpectedClientError("Create request hook failed to execute", {
          cause: err,
        }),
      );
    }

    return OK(new Request(input.url, input.options));
  }

  public async _do(
    request: Request,
    options: {
      context: HookContext;
      isErrorStatusCode: (statusCode: number) => boolean;
      retryConfig: RetryConfig;
      retryCodes: string[];
    },
  ): Promise<
    Result<
      Response,
      | RequestAbortedError
      | RequestTimeoutError
      | ConnectionError
      | UnexpectedClientError
    >
  > {
    const { context, isErrorStatusCode } = options;
    const timeout_ms = context.timeout_ms;

    return retry(
      async () => {
        const cloned = request.clone();
        let attempt = cloned;
        if (timeout_ms != null && timeout_ms > 0) {
          const timeoutSignal = AbortSignal.timeout(timeout_ms);
          const combined = combineSignals(cloned.signal, timeoutSignal)
            ?? timeoutSignal;
          attempt = new Request(cloned, { signal: combined });
        }
        const req = await this._hooks.beforeRequest(context, attempt);
        await logRequest(this._logger, req).catch((e) =>
          this._logger?.log("Failed to log request:", e)
        );

        let response = await this._httpClient.request(req);

        try {
          if (isErrorStatusCode(response.status)) {
            const result = await this._hooks.afterError(
              context,
              response,
              null,
            );
            if (result.error) {
              throw result.error;
            }
            response = result.response || response;
          } else {
            response = await this._hooks.afterSuccess(context, response);
          }
        } finally {
          await logResponse(this._logger, response, req)
            .catch(e => this._logger?.log("Failed to log response:", e));
        }

        return response;
      },
      { config: options.retryConfig, statusCodes: options.retryCodes },
    ).then(
      (r) => OK(r),
      (err) => {
        switch (true) {
          case isAbortError(err):
            return ERR(
              new RequestAbortedError("Request aborted by client", {
                cause: err,
              }),
            );
          case isTimeoutError(err):
            return ERR(
              new RequestTimeoutError("Request timed out", { cause: err }),
            );
          case isConnectionError(err):
            return ERR(
              new ConnectionError("Unable to make request", { cause: err }),
            );
          default:
            return ERR(
              new UnexpectedClientError("Unexpected HTTP client error", {
                cause: err,
              }),
            );
        }
      },
    );
  }
}

const jsonLikeContentTypeRE = /^(application|text)\/([^+]+\+)*json.*/;
const jsonlLikeContentTypeRE =
  /^(application|text)\/([^+]+\+)*(jsonl|x-ndjson)\b.*/;
async function logRequest(logger: Logger | undefined, req: Request) {
  if (!logger) {
    return;
  }

  const contentType = req.headers.get("content-type");
  const ct = contentType?.split(";")[0] || "";

  logger.group(`> Request: ${req.method} ${req.url}`);

  logger.group("Headers:");
  for (const [k, v] of req.headers.entries()) {
    logger.log(`${k}: ${v}`);
  }
  logger.groupEnd();

  logger.group("Body:");
  switch (true) {
    case jsonLikeContentTypeRE.test(ct):
      logger.log(await req.clone().json());
      break;
    case ct.startsWith("text/"):
      logger.log(await req.clone().text());
      break;
    case ct === "multipart/form-data": {
      const body = await req.clone().formData();
      for (const [k, v] of body) {
        const vlabel = v instanceof Blob ? "<Blob>" : v;
        logger.log(`${k}: ${vlabel}`);
      }
      break;
    }
    default:
      logger.log(`<${contentType}>`);
      break;
  }
  logger.groupEnd();

  logger.groupEnd();
}

async function logResponse(
  logger: Logger | undefined,
  res: Response,
  req: Request,
) {
  if (!logger) {
    return;
  }

  const contentType = res.headers.get("content-type");
  const ct = contentType?.split(";")[0] || "";

  logger.group(`< Response: ${req.method} ${req.url}`);
  logger.log("Status Code:", res.status, res.statusText);

  logger.group("Headers:");
  for (const [k, v] of res.headers.entries()) {
    logger.log(`${k}: ${v}`);
  }
  logger.groupEnd();

  logger.group("Body:");
  switch (true) {
    case matchContentType(res, "application/json")
      || jsonLikeContentTypeRE.test(ct) && !jsonlLikeContentTypeRE.test(ct):
      logger.log(await res.clone().json());
      break;
    case matchContentType(res, "application/jsonl")
      || jsonlLikeContentTypeRE.test(ct):
    case matchContentType(res, "text/event-stream"):
      logger.log(`<${contentType}>`);
      break;
    case matchContentType(res, "text/*"):
      logger.log(await res.clone().text());
      break;
    case matchContentType(res, "multipart/form-data"): {
      const body = await res.clone().formData();
      for (const [k, v] of body) {
        const vlabel = v instanceof Blob ? "<Blob>" : v;
        logger.log(`${k}: ${vlabel}`);
      }
      break;
    }
    default:
      logger.log(`<${contentType}>`);
      break;
  }
  logger.groupEnd();

  logger.groupEnd();
}
